import { DestroyRef, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationError, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { filter } from 'rxjs';

@Service()
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  init(): void {
    if (this.swUpdate?.isEnabled) {
      // 1. Recover from broken/corrupted Service Worker states (e.g. missing chunks on server)
      this.swUpdate.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
        console.warn('[PwaUpdateService] Service worker unrecoverable state:', event.reason);
        window.location.reload();
      });

      // 2. Immediately activate new downloaded versions and reload
      this.swUpdate.versionUpdates
        .pipe(
          filter((evt) => evt.type === 'VERSION_READY'),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          console.info('[PwaUpdateService] New application version downloaded; activating...');
          void this.swUpdate?.activateUpdate().then(() => {
            window.location.reload();
          });
        });
    }

    // 3. Catch lazy-loaded chunk loading failures during route navigation
    this.router.events
      .pipe(
        filter((e): e is NavigationError => e instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        const msg = e.error?.message || e.error?.toString() || '';
        if (/loading chunk|failed to fetch dynamically imported module/i.test(msg)) {
          console.warn(
            '[PwaUpdateService] Missing bundle chunk detected during navigation, reloading:',
            msg,
          );
          window.location.reload();
        }
      });
  }
}
